/**
 * Adaptador de Google Sheets (sistema de registro autoritativo, ADR OQ-1).
 *
 * INV-6 se hace estructural, no solo convencional: la interfaz no declara
 * ningun metodo de actualizacion ni de borrado sobre `entries`. Cualquier
 * implementacion que respete este tipo es incapaz, por construccion, de
 * sobrescribir o eliminar una fila existente.
 */

export interface SheetRow {
  readonly entryId: string;
  readonly deviceId: string;
  readonly deviceSeq: number;
  readonly date: string;
  readonly description: string;
  readonly origin: string;
  readonly reversesEntryId: string | null;
  readonly createdAt: string;
  readonly postingsJson: string;
  readonly entryHash: string;
  readonly deviceChainHash: string;
}

export class RateLimitError extends Error {
  constructor(message = "Limite de peticiones excedido") {
    super(message);
    this.name = "RateLimitError";
  }
}

export interface SheetsAdapter {
  /** entry_id ya presentes en la hoja — para comprobar antes de anexar (idempotencia, ADR OQ-1 c.1). */
  listExistingEntryIds(): Promise<Set<string>>;
  /** Anexa filas nuevas por lotes. Nunca actualiza ni borra: no existe metodo para ello. */
  appendEntries(rows: readonly SheetRow[]): Promise<void>;
  /** Lee todas las filas de asientos, para reconstruccion y verificacion de integridad. */
  readAllEntries(): Promise<SheetRow[]>;
}

export interface BackoffOptions {
  readonly maxAttempts: number;
  readonly baseDelayMs: number;
  readonly sleep: (ms: number) => Promise<void>;
}

const defaultSleep = (ms: number): Promise<void> => new Promise((resolve) => setTimeout(resolve, ms));

export const defaultBackoffOptions: BackoffOptions = {
  maxAttempts: 5,
  baseDelayMs: 200,
  sleep: defaultSleep,
};

/**
 * Reintenta con retroceso exponencial ante RateLimitError (FR-STORE-9,
 * FR-SYNC-5). Cualquier otro error se propaga de inmediato sin reintentar.
 */
export async function withExponentialBackoff<T>(
  operation: () => Promise<T>,
  options: BackoffOptions = defaultBackoffOptions,
): Promise<T> {
  let attempt = 0;
  for (;;) {
    try {
      return await operation();
    } catch (err) {
      attempt += 1;
      if (!(err instanceof RateLimitError) || attempt >= options.maxAttempts) {
        throw err;
      }
      const delay = options.baseDelayMs * 2 ** (attempt - 1);
      await options.sleep(delay);
    }
  }
}

/** Doble de prueba: en memoria, append-only por construccion (implementa la misma interfaz sin metodos de mutacion). */
export class FakeSheetsAdapter implements SheetsAdapter {
  private readonly rows = new Map<string, SheetRow>();
  private rateLimitedCallsRemaining = 0;

  /** Utilidad de test: fuerza N fallos de cuota antes de aceptar el siguiente appendEntries. */
  simulateRateLimitFor(calls: number): void {
    this.rateLimitedCallsRemaining = calls;
  }

  async listExistingEntryIds(): Promise<Set<string>> {
    return new Set(this.rows.keys());
  }

  async appendEntries(rows: readonly SheetRow[]): Promise<void> {
    if (this.rateLimitedCallsRemaining > 0) {
      this.rateLimitedCallsRemaining -= 1;
      throw new RateLimitError();
    }
    for (const row of rows) {
      if (this.rows.has(row.entryId)) {
        // Insercion idempotente: un entry_id repetido es un no-op, nunca una sobrescritura.
        continue;
      }
      this.rows.set(row.entryId, row);
    }
  }

  async readAllEntries(): Promise<SheetRow[]> {
    return Array.from(this.rows.values());
  }

  /** Solo para tests de deteccion de alteracion externa (INV-9): simula una edicion fuera de la aplicacion. */
  __testOnly_externallyAlterRow(entryId: string, patch: Partial<SheetRow>): void {
    const existing = this.rows.get(entryId);
    if (!existing) throw new Error(`No existe la fila ${entryId}`);
    this.rows.set(entryId, { ...existing, ...patch });
  }

  /** Solo para tests de deteccion de eliminacion externa (INV-9). */
  __testOnly_externallyDeleteRow(entryId: string): void {
    this.rows.delete(entryId);
  }
}
